import axios from 'axios';
import moment from 'moment';
import React, { memo, useEffect, useMemo, useReducer, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';

import { getProjectStatistic } from '../../../api';
import { useConfig } from '../../../contexts/config';
import { useProjects } from '../../../contexts/settings';
import { usePaths } from '../../../routes/routesHelper';
import { useFirstRender } from '../../../utils';
import { CopyIcon, VerticalSeparator } from '../../ui';
import BottomRow from './components/BottomRow';
import StatisticCard from './components/StatisticCard';
import TopRow from './components/TopRow';
import { init, initialState, statisticCardReducer } from './reducer';
import { ProjectIDText, ProjectIDWrapper, StatisticsWrapper, Title, Wrapper } from './style';
import { ActionType } from './types';

type Props = {
  projectId: string;
  name: string;
  dateChanged?: string;
  publisherProjectId: string | null;
};

const LoginProjectCard = ({
  projectId,
  name,
  dateChanged,
  publisherProjectId
}: Props): JSX.Element => {
  const { usersPath } = usePaths();
  const intl = useIntl();
  const { updateProjectStatistic, getProjectById } = useProjects();
  const { xsollaAnalytics } = useConfig();
  const [copied, setCopied] = useState<boolean>(false);
  const firstRender = useFirstRender();

  useEffect(() => {
    if (!firstRender && copied) {
      xsollaAnalytics.sendClick('login_pa_module__dashboard__copy_project_id', projectId);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);

  const [state, dispatch] = useReducer(statisticCardReducer, initialState, () =>
    init(getProjectById(projectId))
  );

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();
    const date = moment().format('YYYY-MM-DD');

    if (!state.currProject?.users) {
      dispatch({ type: ActionType.getDataStart });
      getProjectStatistic(projectId, date, cancelToken.token)
        .then((res) =>
          dispatch({
            type: ActionType.getDataSuccess,
            payload: updateProjectStatistic(projectId, { ...res })
          })
        )
        .catch(() => dispatch({ type: ActionType.getDataFail }));
    } else {
      dispatch({ type: ActionType.stopFetching });
    }

    return () => {
      cancelToken.cancel();
    };
  }, [projectId]);

  const usersCard = useMemo(() => {
    return (
      <StatisticCard
        title={intl.formatMessage({ id: 'dashboard.login-card.statistics.users' })}
        name="user_database"
        number={state.currProject?.users}
        fetching={state.fetching}
        titleLink={usersPath(projectId)}
        error={state.error}
        isAccent
      />
    );
  }, [state.fetching]);

  const mauCard = useMemo(() => {
    return (
      <StatisticCard
        title={intl.formatMessage({ id: 'dashboard.login-card.statistics.mau' })}
        number={state.currProject?.mau?.current}
        numberChange={state.currProject?.mau?.change}
        fetching={state.fetching}
        tooltip={intl.formatMessage({ id: 'dashboard.login-card.statistics.mau.help' })}
        error={state.error}
      />
    );
  }, [state.fetching]);

  const newUsersCard = useMemo(() => {
    return (
      <StatisticCard
        title={intl.formatMessage({ id: 'dashboard.login-card.statistics.new-users' })}
        number={state.currProject?.new_users?.current}
        numberChange={state.currProject?.new_users?.change}
        fetching={state.fetching}
        tooltip={intl.formatMessage({ id: 'dashboard.login-card.statistics.new-users.help' })}
        error={state.error}
      />
    );
  }, [state.fetching]);

  return (
    <Wrapper>
      <TopRow date={dateChanged} projectId={projectId} />

      <Title>
        {`${name}`}
        <CopyToClipboard text={projectId}>
          <ProjectIDWrapper onClick={() => setCopied(true)}>
            <ProjectIDText>
              {intl.formatMessage({ id: 'dashboard.login-card.loginId' })}:
            </ProjectIDText>
            &nbsp;{projectId}
            <CopyIcon disabled={copied} />
          </ProjectIDWrapper>
        </CopyToClipboard>
        {publisherProjectId && (
          <CopyToClipboard text={publisherProjectId}>
            <ProjectIDWrapper onClick={() => setCopied(true)}>
              <ProjectIDText>
                {intl.formatMessage({ id: 'dashboard.login-card.publishertId' })}:
              </ProjectIDText>
              &nbsp;{publisherProjectId}
              <CopyIcon disabled={copied} />
            </ProjectIDWrapper>
          </CopyToClipboard>
        )}
      </Title>

      <StatisticsWrapper>
        {usersCard}
        <VerticalSeparator height="40px" />
        {mauCard}
        <VerticalSeparator height="40px" />
        {newUsersCard}
      </StatisticsWrapper>

      <BottomRow projectId={projectId} metricsName="dashboard__configure_click" />
    </Wrapper>
  );
};

export default memo(LoginProjectCard);
